import { mealWeights } from './intake.js';
const STATUS_LABEL = {
    given: '기록함', skipped: '건너뜀', refused: '거부', partial: '일부 기록',
};
function dayStart(value) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date(value));
    return Date.parse(`${parts}T00:00:00+09:00`);
}
function finiteTimestamp(value) {
    return Number.isFinite(value) && value >= 0;
}
function cadenceStart(cadence, to, visits) {
    const today = dayStart(to);
    if (cadence === 'day')
        return { from: today };
    if (cadence === 'week')
        return { from: today - 6 * 86_400_000 };
    const latestVisit = visits
        .filter((visit) => finiteTimestamp(visit.visitedAt) && visit.visitedAt <= to && !visit.deletedAt)
        .sort((left, right) => right.visitedAt - left.visitedAt || left.id.localeCompare(right.id))[0];
    return latestVisit ? { from: latestVisit.visitedAt, visitBoundary: latestVisit.visitedAt } : { from: today - 29 * 86_400_000 };
}
/**
 * Turns existing device-local records into a factual timeline. It performs no
 * image recognition, diagnosis, severity scoring, prognosis or appearance
 * comparison. Photos are only local attachment references chosen at capture.
 */
export function buildCareTimeline(input) {
    if (!finiteTimestamp(input.to))
        throw new RangeError('timeline end is invalid');
    const range = cadenceStart(input.cadence, input.to, input.visits.filter((item) => item.petId === input.petId));
    const inRange = (value) => finiteTimestamp(value) && value >= range.from && value <= input.to;
    const treatmentById = new Map(input.treatments.filter((item) => item.petId === input.petId).map((item) => [item.id, item]));
    const treatmentFacts = input.treatmentLogs
        .filter((item) => item.petId === input.petId && !item.deletedAt && !item.duplicateOf && inRange(item.recordedAt))
        .map((item) => {
        const treatment = treatmentById.get(item.treatmentId);
        return {
            id: `treatment:${item.id}`,
            kind: 'treatment',
            occurredAt: item.recordedAt,
            title: treatment?.name ?? '투약·처치 기록',
            detail: `${STATUS_LABEL[item.status]}${item.actorLabel ? ` · ${item.actorLabel} 기록` : ''}`,
            ...(item.note?.trim() ? { note: item.note.trim() } : {}),
            attachmentIds: [],
        };
    });
    const mealFacts = input.meals
        .filter((item) => item.petId === input.petId && !item.deletedAt && !item.duplicateOf && inRange(item.servedAt))
        .map((item) => ({
        id: `meal:${item.id}`,
        kind: 'meal',
        occurredAt: item.servedAt,
        title: item.foodName || '식사 기록',
        detail: `준 양 ${mealWeights(item).servedG}g${mealWeights(item).leftoverG == null ? '' : ` · 남은 양 ${mealWeights(item).leftoverG}g`} · ${item.waterMl == null ? '물 미관찰' : `물 ${item.waterMl}ml`}`,
        attachmentIds: [],
    }));
    const activityFacts = input.gaitLogs
        .filter((item) => item.petId === input.petId && !item.deletedAt && !item.duplicateOf && inRange(item.startedAt))
        .map((item) => ({
        id: `activity:${item.id}`,
        kind: 'activity',
        occurredAt: item.startedAt,
        title: '활동 기록',
        detail: `${Math.max(0, Math.round(item.durationSec / 60))}분${item.distanceM == null ? '' : ` · ${item.distanceM}m`}${item.stoppedEarly ? ' · 중간에 마침' : ''}`,
        attachmentIds: [],
    }));
    const sceneFacts = input.events
        .filter((item) => item.petId === input.petId && !item.deletedAt && !item.duplicateOf && inRange(item.occurredAt))
        .map((item) => ({
        id: `scene:${item.id}`,
        kind: 'scene',
        occurredAt: item.occurredAt,
        title: '돌봄 장면',
        detail: item.durationSec == null ? '보호자가 남긴 사건 기록' : `지속 시간 ${item.durationSec}초`,
        ...(item.severityNote?.trim() ? { note: item.severityNote.trim() } : {}),
        attachmentIds: [...(item.attachmentIds ?? [])],
    }));
    return {
        cadence: input.cadence,
        from: range.from,
        to: input.to,
        ...(range.visitBoundary == null ? {} : { visitBoundary: range.visitBoundary }),
        facts: [...treatmentFacts, ...mealFacts, ...activityFacts, ...sceneFacts]
            .sort((left, right) => left.occurredAt - right.occurredAt || left.id.localeCompare(right.id)),
    };
}
function singleLine(value, maximum = 240) {
    const result = value.normalize('NFC').replace(/\s+/gu, ' ').trim();
    if (!result || result.length > maximum)
        throw new RangeError('timeline text is invalid');
    return result;
}
/** Exports only facts the user explicitly selected on the current range. */
export function buildCareTimelineExport(input) {
    const selected = input.facts.filter((item) => input.selectedIds.has(item.id));
    if (selected.length < 1 || selected.length > 100)
        throw new RangeError('timeline selection is invalid');
    const lines = [
        `[펫리듬] ${singleLine(input.petName, 20)} 재진 타임라인`,
        singleLine(input.rangeLabel, 80),
        '',
        ...selected.flatMap((fact) => [
            `${singleLine(input.formatDateTime(fact.occurredAt), 60)} · ${singleLine(fact.title, 80)}`,
            `- ${singleLine(fact.detail)}`,
            ...(fact.note ? [`- 메모: ${singleLine(fact.note)}`] : []),
        ]),
        '',
        '사용자가 직접 고른 기록만 포함했습니다. 사진을 판독하거나 상태를 판단하지 않습니다.',
    ];
    return lines.join('\n');
}
